import {
  columnNamesType,
  OctTable
} from "@oceantech/oceantech-ui";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { AppContext } from "../../appContext/AppContext";
import { TextFieldCustom } from "../../category-personnel/components/text-field-custom/TextFieldCustom";
import AutocompleteV2 from "../../component/AutocompleteObjectV2";
import { generateYears } from "../../utils/AppFunction";
import { CODE, RESPONSE_MESSAGE } from "../../utils/Constant";
import { saveHospitalCouncil, getListEmployee, searchHospitalCouncil } from "./services";
import { ConfirmDialog } from "../../component/ConfirmDialog";
import { Form, Formik } from "formik";
import AccountDialog from "./components/AccountDialog";

export const HospitalCouncilManager: FC = () => {
  const { setIsLoading } = useContext(AppContext);

  const [memberSelected, setMemberSelected] = useState<any>(null);
  const [usernameSelected, setUsernameSelected] = useState<string | null>(null);
  const [yearSelected, setYearSelected] = useState<any>(null);
  const [reloadListEmployee, setReloadListEmployee] = useState<boolean>(false);
  const [listMember, setListMember] = useState<any[]>([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [openAccountDialog, setOpenAccountDialog] = useState<boolean>(false);
  const formRef = useRef<any>(null);

  const validationSchema = Yup.object().shape({
    year: Yup.object().nullable().required("Vui lòng chọn năm"),
  });

  const handleGetListMember = async () => {
    try {
      setIsLoading(true);
      let { data } = await searchHospitalCouncil(yearSelected);
      if (CODE.SUCCESS === data?.code) {
        setListMember(data?.data?.content);
      } else {
        let errorMesage = data?.data?.[0]?.message || RESPONSE_MESSAGE.ERROR;
        toast.warning(errorMesage);
      }
    } catch (error) {
      toast.warning(RESPONSE_MESSAGE.ERROR);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    handleGetListMember();
  }, [yearSelected]);
  
  const handleAddMember = () => {
    if (usernameSelected) {
      let member = listMember.map((item) => {
        if (usernameSelected === item?.username) {
          return memberSelected;
        } else {
          return item;
        }
      });
      setListMember(member || []);
    } else {
      memberSelected && setListMember([...listMember, memberSelected]);
    }
    setMemberSelected(null);
    setUsernameSelected(null);
  };

  const handleDeleteMember = () => {
    setListMember(listMember.filter((item) => item?.username !== usernameSelected));
    setUsernameSelected(null);
  };

  const handleTextChange = (e: any) => {
    const { name, value } = e.target ?? {};
    setMemberSelected((prev: any) => ({ ...prev, [name]: value }));
  }

  const handleFormSubmit = async (values: any) => {
    const submitData = {
      year: values.year?.id,
      members: listMember,
    }
    try {
      setIsLoading(true);
      let { data } = await saveHospitalCouncil(submitData);
      if (CODE.SUCCESS === data?.code) {
        setIsLoading(false);
        toast.success("Thêm hội đồng thành công");
      } else {
        setIsLoading(false);
        let errorMesage = data?.data?.[0]?.message || "Xảy ra lỗi, vui lòng thử lại!";
        toast.warning(errorMesage);
      }
    } catch (error) {
      toast.warning("Xảy ra lỗi, vui lòng thử lại!");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns: columnNamesType[] = [
    {
      name: "STT",
      field: "stt",
      headerStyle: {
        width: "50px",
      },
      cellStyle: {
        textAlign: "center",
      },
      render: (row, index, stt) => {
        return (
          <span>{stt}</span>
        );
      },
    },
    {
      name: "Tên thành viên",
      field: "name",
      headerStyle: {
        minWidth: "350px",
      },
      cellStyle: {
        textAlign: "left",
      },
    },
    {
      name: "Khoa phòng",
      field: "department",
      headerStyle: {
        minWidth: "150px",
      },
      cellStyle: {
        textAlign: "left",
      },
    },
    {
      name: "Chức vụ",
      field: "position",
      headerStyle: {
        minWidth: "100px",
      },
      cellStyle: {
        textAlign: "center",
      },
    },
    {
      name: "Thao tác",
      field: "action",
      headerStyle: {
        width: "150px",
        maxWidth: "150px",
      },
      render: (row, index, stt) => {
        return (
          <div className="flex justify-content-center align-items-center gap-4 px-4">
            <span
              className="tooltips"
              data-tooltip="Chỉnh sửa"
              data-tooltip-pos="left"
              data-tooltip-length="small"
            >
              <i
                className="text-pri bi bi-pencil-fill fs-2"
                onClick={() => {
                  setUsernameSelected(row?.username);
                  setMemberSelected(row);
                }}
              ></i>
            </span>
            <span
              className="tooltips"
              data-tooltip="Xóa"
              data-tooltip-pos="left"
              data-tooltip-length="small"
            >
              <i
                className="text-danger bi bi-trash3 fs-2"
                onClick={() => {
                  setUsernameSelected(row?.username);
                  setOpenConfirmDialog(true);
                }}
              ></i>
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white">
      <div className="spaces bg-gray radius-2">
        <div className="bg-white spaces width-100 d-flex justify-content-between align-items-center p-10 border-bottom">
          <h4 className="text-title text-pri fw-bold fs-4 spaces mb-0">
            Danh thành viên hội đồng nghiên cứu khoa học bệnh viện
          </h4>
          <Button className="btn-fill spaces min-w-240 h-34" onClick={() => setOpenAccountDialog(true)}>
            Tạo tài khoản thành viên hội đồng
          </Button>
        </div>
        <div className="bg-white spaces width-100 p-10">
          <Formik<any>
            enableReinitialize
            initialValues={{ year: generateYears()?.[0] || null }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              handleFormSubmit(values);
              setSubmitting(false);
            }}
            innerRef={formRef}
          >
            {({
              values,
              touched,
              errors,
              handleSubmit,
              setFieldValue,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <div className="form spaces p-10">
                    <Row className="py-2">
                      <Col xs={4} className="border-bottom spaces mb-0 px-10 pb-10">
                        <div className="flex align-items-center px-2">
                          <div className="label min-w-100px fw-bold">
                            Năm
                          </div>
                          <div className="flex-1">
                            <AutocompleteV2
                              options={generateYears()}
                              name={"year"}
                              onChange={(option: any) => {
                                setFieldValue("year", option);
                                setYearSelected(option?.id);
                              }}
                              value={values.year}
                              errors={errors.year}
                              touched={touched.year}
                              getOptionLabel={(e: any) => e?.title}
                              isClearable={false}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Row className="py-2">
                      <Col xs={4}>
                        <div className="flex align-items-center px-2">
                          <div className="label min-w-100px fw-bold">
                            Họ tên
                          </div>
                          <div className="flex-1">
                            <AutocompleteV2
                              options={[]}
                              name="name"
                              onChange={(option: any) => {
                                setMemberSelected(option);
                              }}
                              searchFunction={getListEmployee}
                              searchObject={{}}
                              value={memberSelected}
                              getOptionLabel={(e: any) => e?.name}
                              dependencies={[reloadListEmployee]}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div className="flex align-items-center">
                          <div className="label min-w-100px fw-bold">
                            Khoa phòng
                          </div>
                          <div className="flex-1">
                            <TextFieldCustom
                              type="text"
                              name="department"
                              values={memberSelected?.department ?? ""}
                              handleChange={handleTextChange}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div className="flex align-items-center">
                          <div className="label min-w-100px fw-bold">
                            Chức vụ
                          </div>
                          <div className="flex-1">
                            <TextFieldCustom
                              type="text"
                              name="position"
                              values={memberSelected?.position ?? ""}
                              handleChange={handleTextChange}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div className="flex align-items-center px-2 pt-3">
                          <div className="label min-w-100px fw-bold">
                            Điện thoại
                          </div>
                          <div className="flex-1">
                            <TextFieldCustom
                              type="text"
                              name="phone"
                              values={memberSelected?.phone ?? ""}
                              handleChange={handleTextChange}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div className="flex align-items-center pt-3">
                          <div className="label min-w-100px fw-bold">Email</div>
                          <div className="flex-1">
                            <TextFieldCustom
                              type="text"
                              name="email"
                              values={memberSelected?.email ?? ""}
                              handleChange={handleTextChange}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col xs={4}>
                        {memberSelected && (
                          <Button
                            className="btn-fill spaces min-w-80 mt-13"
                            onClick={() => handleAddMember()}
                          >
                            {usernameSelected ? "Cập nhật" : "Thêm"}
                          </Button>
                        )}
                      </Col>
                    </Row>
                    <div className="d-flex flex-column spaces width-100 mt-20 h-calc-vh-362">
                      <OctTable
                        columns={columns}
                        data={listMember || []}
                        justFilter={false}
                        clearToolbar
                        noToolbar
                        noPagination
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end spaces pt-15 pb-5 border-top">
                    <Button className="btn-fill spaces min-w-80" type="submit">
                      Lưu
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
        {openAccountDialog && (
          <AccountDialog
            show={openAccountDialog}
            onHide={() => setOpenAccountDialog(false)}
            onRefresh={() => setReloadListEmployee(!reloadListEmployee)}
          />
        )}
        {openConfirmDialog && (
          <ConfirmDialog
            show={openConfirmDialog}
            yes="Xác nhận"
            onYesClick={handleDeleteMember}
            message={`Bạn có chắc muốn xóa thành viên này?`}
            close="Huỷ"
            onCloseClick={() => setOpenConfirmDialog(false)}
            title="Xác nhận thao tác"
          />
        )}
      </div>
    </div>
  );
};

export default HospitalCouncilManager;
